import useSWR, { type KeyedMutator } from "swr";
import { useApiClient } from "@/hooks/useApiClient";
import {
  getTransferEligibility,
  TRANSFER_ELIGIBILITY_ENDPOINT,
} from "@/features/salesforceIntegration/api/getTransferEligibility";
import {
  type TransferEligibilityResponse,
  type TransferEligibilityResult,
} from "@/features/salesforceIntegration/types";

export interface UseTransferEligibilityResult {
  results: TransferEligibilityResult[];
  isLoading: boolean;
  /**
   * 直近の取得に失敗したか。`mutate()`は再検証に失敗しても解決するため、
   * 取り直しの成否はこの値で見る。
   */
  error: Error | undefined;
  mutate: KeyedMutator<TransferEligibilityResponse>;
}

/**
 * 連携可否の判定結果を取得するhook。
 * 一覧画面は表示中の行のIDをまとめて1回で取得し（`includeRealEstates`は付けない）、
 * 詳細画面は対象1件のIDに`includeRealEstates=true`を付けて呼ぶ（実装計画書7.4節）。
 *
 * `pictureRequestIds`が空の場合はフェッチしない
 * （連携ボタン非表示時や一覧未ロード時に無駄なリクエストを送らないため）。
 */
export const useTransferEligibility = (
  pictureRequestIds: number[],
  includeRealEstates = false
): UseTransferEligibilityResult => {
  const { apiClient } = useApiClient();

  // IDは順序が変わっても結果が同じため、キャッシュを効かせるために並べ替える。
  // キーだけでなく実リクエストにも同じ並びを渡し、キーと送信内容を一致させる
  // （別々に扱うとキーとリクエストの対応が読み取れず、デバッグ時に混乱する）。
  const sortedPictureRequestIds = pictureRequestIds
    .slice()
    .sort((a, b) => a - b);

  // キーは実際に送るクエリパラメータ名で組み立てる。
  const key =
    sortedPictureRequestIds.length === 0
      ? null
      : `${TRANSFER_ELIGIBILITY_ENDPOINT}?pictureRequestIds=${sortedPictureRequestIds.join(
          ","
        )}&includeRealEstates=${String(includeRealEstates)}`;

  const { data, isLoading, error, mutate } = useSWR<
    TransferEligibilityResponse,
    Error
  >(
    key,
    async () =>
      await getTransferEligibility(apiClient, {
        pictureRequestIds: sortedPictureRequestIds,
        includeRealEstates,
      }),
    { revalidateOnFocus: false }
  );

  return {
    results: data?.results ?? [],
    isLoading,
    error,
    mutate,
  };
};
